import {
  EXPLORER_UPLOADS_ADD,
  EXPLORER_UPLOADS_REMOVE,
} from '@disco/constants/ActionTypes'
import {
  SORT_DIRECTION_DESC,
  TRACK_DATE_SORT,
} from '@disco/constants/SortTypes'
import { TRACK_VIEW } from '@disco/constants/ViewTypes'
import { pushUINotification } from '@disco/utils/notifications'
import { plural } from '@disco/utils/string-utils'
import { uploadFile, taskRunner } from '@disco/utils/upload'
import difference from 'lodash/difference'
import * as React from 'react'
import type { Group } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { GetState } from 'types/redux'
import type { TempItem } from 'types/shortlist'
import type { NestingLabel, Track } from 'types/track'
import {
  addTracksToLibrary,
  alterTrackGroups,
  appendTracksToPlaylist,
  switchPaneView,
} from '@disco/state-management/actions/ExplorerActions'
import { addNestedTracksToMain } from '@disco/state-management/actions/NestingActions'
import {
  getOrFetchTrack,
  mapResponseToReducerObjects,
} from '@disco/state-management/actions/PlaylistTrackActions'
import { prepareTempItems } from '@disco/utils/upload'
import { verifyFiles } from '@disco/utils/upload'
import { getTeamLibrary } from '@disco/state-management/selectors/groups'
import { getTrack } from '@disco/state-management/selectors/tracks'
/**
 * Adds upload IDs to the explorer uploads tracking.
 * @param ids - Array of upload IDs to add
 * @returns Action object to add uploads to explorer state
 */
export const addExplorerUploads = (
  ids: string[],
): {
  ids: Array<string>
  type: string
} => ({
  type: EXPLORER_UPLOADS_ADD,
  ids,
})
/**
 * Removes upload IDs from the explorer uploads tracking.
 * @param ids - Array of upload IDs to remove
 * @returns Action object to remove uploads from explorer state
 */
export const removeExplorerUploads = (
  ids: string[],
): {
  ids: Array<string>
  type: string
} => ({
  type: EXPLORER_UPLOADS_REMOVE,
  ids,
})
/**
 * Uploads files using a specified handler function with parameters.
 * @param files - Array of files to upload
 * @param func - Upload handler function to process each file
 * @param params - Additional parameters to pass to the handler function
 * @returns Thunk that uploads files using task runner and returns settled promises
 */
export const uploadFiles =
  (
    files: File[],
    func: (...args: Array<any>) => any,
    params: any[],
  ): ((dispatch: any, getState: GetState) => Promise<any>) =>
  (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { csrfToken } = getState().user
    // Prepare temp items and verify files
    const tempItems = verifyFiles(prepareTempItems(files), pushUINotification)
    dispatch(addExplorerUploads(tempItems.map(({ tempId }) => tempId)))
    const tasks = tempItems.map(tempItem =>
      taskRunner.addTask({
        id: tempItem.tempId,
        func,
        params: [dispatch, getState, csrfToken, tempItem, ...params],
      }),
    )
    return Promise.allSettled(tasks)
  }

function uploadFileHandler(
  dispatch: (...args: Array<any>) => any,
  getState: GetState,
  csrfToken: string,
  tempItem: TempItem,
  action?: (...rest: any[]) => any,
  ...rest
) {
  return uploadFile(
    tempItem.file,
    csrfToken,
    tempItem.tempId,
    {
      onDuplicateFound(track, _info) {
        dispatch(removeExplorerUploads([tempItem.tempId]))
        const normalizedTrack = mapResponseToReducerObjects(
          dispatch,
          getState,
          track,
        )
        if (action) action(...rest, normalizedTrack)
      },

      onUploadDone(track, tempTrack) {
        dispatch(removeExplorerUploads([tempTrack.tempId]))
        const { title, artist, album } = tempTrack
        // if uploaded is not set, that means that server didn't read tags
        // yet, use parsed tags from frontend instead
        const adjustedTrack = track.uploaded
          ? track
          : { ...track, name: title, artist, album, ext: tempItem.ext }
        const normalizedTrack = mapResponseToReducerObjects(
          dispatch,
          getState,
          adjustedTrack,
        )
        if (action) action(...rest, normalizedTrack)
      },

      onUploadError(tempTrack) {
        dispatch(removeExplorerUploads([tempTrack.tempId]))
      },
    },
    null,
    {},
  )
}

/**
 * Uploads files and assigns them to specified groups.
 * @param files - Array of files to upload
 * @param targetGroups - Array of groups to assign uploaded files to
 * @returns Thunk that uploads files and adds them to groups with duplicate handling
 */
export const uploadFilesToGroups =
  (files: File[], targetGroups: Group[]): ((dispatch: any) => Promise<void>) =>
  async (dispatch: (...args: Array<any>) => any) => {
    const taskResults = await dispatch(
      uploadFiles(files, uploadFileToGroups, []),
    )
    const { successes, duplicates } = getTaskResults(taskResults)
    const { toProcess } = notifyAboutDuplicates(
      dispatch,
      duplicates,
      (t: Track) =>
        difference(
          targetGroups.map(g => g.url),
          t.groups,
        ).length > 0,
      targetGroups.map(g => g.name).join(','),
    )
    const tracksToAdd = [...successes, ...toProcess]

    if (tracksToAdd.length > 0) {
      await dispatch(
        alterTrackGroups([...successes, ...toProcess], targetGroups, [], {
          disableNotifications: true,
        }),
      )
    }

    pushTaskUpdates(taskResults)
  }

const uploadFileToGroups = (dispatch, getState, csrfToken, tempItem, groups) =>
  uploadFileHandler(dispatch, getState, csrfToken, tempItem, () => {}, groups)

export type UploadFilesToMainTrack = (
  targetTrack: Track,
  files: File[],
  nestingLabel: NestingLabel,
  nestingPosition: number,
) => (
  dispatch: (...args: Array<any>) => any,
  getState: GetState,
) => Promise<void>
/**
 * Uploads files as nested tracks to a main track.
 * @param targetTrack - Target track to nest uploaded files to
 * @param files - Array of files to upload as nested tracks
 * @param nestingLabel - Label for the nesting relationship
 * @param nestingPosition - Position in the nesting order (defaults to 0)
 * @returns Thunk that uploads files and nests them to the main track
 */
export const uploadFilesToMainTrack: UploadFilesToMainTrack =
  (
    targetTrack: Track,
    files: File[],
    nestingLabel: NestingLabel,
    nestingPosition: number = 0,
  ) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    let mainTrack = targetTrack

    if (targetTrack.nesting_parent_id) {
      try {
        mainTrack = await dispatch(
          getOrFetchTrack(targetTrack.nesting_parent_id),
        )
      } catch (_e) {
        return pushUINotification({
          level: 'error',
          message: `Unable to fetch parent track!`,
        })
      }
    }

    const taskResults = await dispatch(uploadFiles(files, uploadNestedFile, []))
    const { successes, duplicates } = getTaskResults(taskResults)
    // server returns bare track in response without any useful data apart of id,
    // use track from reducer that was injected with info from the original file,
    // we need extension to pass through filter in addNestedTracksToMain
    const state = getState()
    const uploadedTracks = successes
      .map(({ id }) =>
        getTrack(state, {
          id,
        }),
      )
      .filter(track => track !== null && track !== undefined)
    const { toProcess } = notifyAboutDuplicates(
      dispatch,
      duplicates,
      (t: Track) => t.nesting_parent_id !== mainTrack.id,
      mainTrack.name,
    )
    const tracksToNest = [...uploadedTracks, ...toProcess]

    if (tracksToNest.length > 0) {
      await dispatch(
        addNestedTracksToMain(
          mainTrack,
          tracksToNest,
          nestingLabel,
          nestingPosition,
        ),
      )
    }

    pushTaskUpdates(taskResults)
  }

const uploadNestedFile = (
  dispatch: (...args: Array<any>) => any,
  getState: GetState,
  csrfToken: string,
  tempItem: TempItem,
) => uploadFileHandler(dispatch, getState, csrfToken, tempItem, () => {})

/**
 * Uploads files to the browse/team library with view switching.
 * @param files - Array of files to upload to browse
 * @returns Thunk that uploads files to team library and switches to track view
 */
export const uploadFilesToBrowse =
  (files: File[]): ((dispatch: any, getState: GetState) => Promise<void>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const taskResults = await dispatch(
      uploadFiles(files, uploadFileToBrowse, []),
    )
    const state = getState()
    const teamLib = getTeamLibrary(state)
    const library = state.app.selectedLibrary
    const { duplicates } = getTaskResults(taskResults)

    if (duplicates.length > 0 && teamLib) {
      const { toProcess } = notifyAboutDuplicates(
        dispatch,
        duplicates,
        (t: Track) => t.groups.includes(teamLib.url),
        'Browse',
      )

      if (toProcess.length > 0) {
        await dispatch(addTracksToLibrary(toProcess, true))
      }
    }

    // reload pane with track view sorted by new tracks on top
    // if user haven't navigated away from "Browse"
    if (library && teamLib && teamLib?.id === library?.id) {
      dispatch(
        switchPaneView(library.panes[0], TRACK_VIEW, {
          sortParam: TRACK_DATE_SORT,
          sortDirection: SORT_DIRECTION_DESC,
        }),
      )
    }

    pushTaskUpdates(taskResults)
  }

const uploadFileToBrowse = (dispatch, getState, csrfToken, tempItem) =>
  uploadFileHandler(dispatch, getState, csrfToken, tempItem, () => {})

type PromiseFulfilledResult<T> = {
  status: 'fulfilled'
  value: T
}
type PromiseRejectedResult = {
  status: 'rejected'
  reason: any
}
type PromiseSettledResult<T> = PromiseFulfilledResult<T> | PromiseRejectedResult

const getTaskResults = (taskPromisesResults: PromiseSettledResult<any>[]) => {
  const values: any[] = taskPromisesResults
    .filter(r => r.status === 'fulfilled')
    .map((r: any): PromiseFulfilledResult<any> => r.value)
  const successes = values.filter(v => Boolean(v.result)).map(v => v.result)
  const failures = values.filter(v => Boolean(v.uploadError))
  const duplicates = values
    .filter(v => Boolean(v.duplicate))
    .map(v => v.duplicate)
  return {
    successes,
    failures,
    duplicates,
  }
}

const pushTaskUpdates = (taskPromisesResults: PromiseSettledResult<any>[]) => {
  const { successes, failures } = getTaskResults(taskPromisesResults)

  if (successes.length === 1 && successes[0].id) {
    const trackId = successes[0].id
    pushUINotification({
      level: 'success',
      message: `Track uploaded!`,
      action: {
        label: 'View track info',
        link: `/explorer/tracks/${trackId}/edit-metadata`,
      },
    })
  } else if (successes.length > 1) {
    pushUINotification({
      level: 'success',
      message: `Your tracks have been uploaded!`,
    })
  }

  if (failures.length > 0) {
    const failedTracks: string[] = failures.map(({ info }) => info.filename)
    pushUINotification({
      level: 'error',
      dismissible: 'both',
      message: `The following files failed to upload:`,
      children: <NotificationStringList list={failedTracks} />,
    })
  }
}

const getDuplicatesMessage = (
  duplicates: number,
  toProcessCount: number,
  toIgnoreCount: number,
  target: string,
) => {
  let message = ''

  if (toProcessCount > 0 && toIgnoreCount > 0) {
    message = `${plural(duplicates, 'track', 'tracks')} from your upload ${
      duplicates === 1 ? 'is' : 'are'
    } already present on server. ${toIgnoreCount} ${
      toIgnoreCount === 1 ? 'is' : 'are'
    } already in ${target}, others will be added there. Here's the list of files that are already present: `
  } else if (toIgnoreCount > 0) {
    message = `${plural(toIgnoreCount, 'track', 'tracks')} from your upload ${
      toIgnoreCount === 1 ? 'is' : 'are'
    } already present in ${target}:`
  } else if (toProcessCount > 0) {
    message = `${plural(toProcessCount, 'track', 'tracks')} from your upload ${
      toProcessCount === 1 ? 'is' : 'are'
    } already present on server and will be added to ${target}:`
  }

  return message
}

// TODO: refactoring might be needed, as this may break mobile app if it'll use these actions
// as action pass DOM specific component
const NotificationStringList = ({ list }: { list: string[] }) =>
  list.map((t, index) => (
    <React.Fragment key={index}>
      {t}
      <br />
    </React.Fragment>
  ))

type TrackAcceptFunction = (t: Track) => boolean

const splitDuplicates = (
  duplicates: Track[],
  acceptFunction: TrackAcceptFunction,
) => {
  const toProcess = duplicates.filter(acceptFunction)
  const toIgnore = duplicates.filter(t => !acceptFunction(t))
  return {
    toProcess,
    toIgnore,
  }
}

const notifyAboutDuplicates = (
  _dispatch,
  duplicates: Track[],
  acceptFunction: TrackAcceptFunction,
  target: string,
) => {
  const { toProcess, toIgnore } = splitDuplicates(duplicates, acceptFunction)

  if (duplicates.length > 0) {
    pushUINotification({
      level: 'success',
      dismissible: 'click',
      message: getDuplicatesMessage(
        duplicates.length,
        toProcess.length,
        toIgnore.length,
        target,
      ),
      children: <NotificationStringList list={duplicates.map(t => t.name)} />,
    })
  }

  return {
    toProcess,
    toIgnore,
  }
}

/**
 * Creates an upload handler with a custom action callback.
 * @param action - Action function to execute after upload completion
 * @returns Upload handler function that executes the action after upload
 */
export const uploadFileWithAction =
  (
    action: (...args: Array<any>) => any,
  ): ((
    dispatch: any,
    getState: GetState,
    csrfToken: string,
    tempItem: TempItem,
  ) => Promise<unknown>) =>
  (
    dispatch: (...args: Array<any>) => any,
    getState: GetState,
    csrfToken: string,
    tempItem: TempItem,
  ) =>
    uploadFileHandler(dispatch, getState, csrfToken, tempItem, action)
/**
 * Uploads files and adds them to a specified playlist.
 * @param files - Array of files to upload
 * @param playlist - Playlist to add uploaded files to
 * @returns Thunk that uploads files and appends them to the playlist
 */
export const uploadFilesToPlaylist =
  (files: File[], playlist: Playlist): ((dispatch: any) => Promise<void>) =>
  async (dispatch: (...args: Array<any>) => any) => {
    const taskResults = await dispatch(
      uploadFiles(files, uploadFileToPlaylist, []),
    )
    const { successes, duplicates } = getTaskResults(taskResults)
    const tracksToAdd = [...successes, ...duplicates]

    if (tracksToAdd.length > 0) {
      dispatch(appendTracksToPlaylist(playlist, tracksToAdd))
    }

    pushTaskUpdates(taskResults)
  }

const uploadFileToPlaylist = (dispatch, getState, csrfToken, tempItem) =>
  uploadFileHandler(dispatch, getState, csrfToken, tempItem, () => {})
